import { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import type { ColumnDef, Row } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { DataTable } from "./data-table";
import {
  DataToolbar,
  type DataToolbarViewMode,
} from "@/components/common/DataToolbar";
import { ColumnSelector } from "@/components/common/ColumnSelector";
import {
  getInitialVisibility,
  getInitialOrder,
} from "@/components/common/column-visibility";
import { WorkingTablePagination } from "./working-table-pagination";
import { useDebounce } from "use-debounce";
import { useLingui } from "@lingui/react/macro";

export interface ApiDatatableBaseParams {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface ApiDatatableMeta {
  totalPages: number;
  total?: number;
}

export interface ApiDatatableProps<
  TData,
  TParams extends ApiDatatableBaseParams,
> {
  searchParams: TParams;
  /** Called for every URL-driven state change (pagination, sorting, search). Wrap navigate with replace:true for clean history. */
  onNavigate: (updater: (prev: TParams) => TParams) => void;
  queryKey: unknown[];
  queryFn: (
    params: TParams,
  ) => Promise<{ data: TData[]; meta: ApiDatatableMeta }>;
  columns: ColumnDef<TData, unknown>[];
  /** If provided, shows the tile view toggle and renders each item with this function. */
  renderTile?: (item: TData, index: number) => ReactNode;
  /**
   * Opt-in controlled view mode, for a caller that stores the choice somewhere
   * (a user preference). Omit both and the table keeps its own state, starting
   * on the table view.
   */
  viewMode?: DataToolbarViewMode;
  onViewModeChange?: (mode: DataToolbarViewMode) => void;
  /** Extracts a stable key for each row in tile view. Falls back to index. */
  getRowId?: (row: TData, index: number) => string;
  onRowClick?: (row: TData) => void;
  /** Extra controls rendered inside the toolbar (filters, selects, etc.). */
  toolbarExtra?: ReactNode;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  emptyMessage?: string;
  /**
   * Rendered in place of `emptyMessage` when the query comes back empty. A
   * filtered-to-nothing list is usually a place to start, not a dead end
   * (T-905).
   */
  emptyState?: ReactNode;
  enabled?: boolean;
  /** Map a row to its sub-rows for tree/accordion expansion. */
  getSubRows?: (row: TData) => TData[] | undefined;
  /** Render a footer row after the last sub-row of an expanded parent. */
  renderAfterSubRows?: (parentRow: Row<TData>) => ReactNode;
  /** When true, prepends a checkbox column so rows can be multi-selected. */
  enableRowSelection?: boolean;
  rowSelection?: Record<string, boolean>;
  onRowSelectionChange?: (selection: Record<string, boolean>) => void;
  /** Optional client-side filter applied after the API response. */
  filterFn?: (rows: TData[]) => TData[];
  /** Called whenever the current page of rows changes. Useful for caching row data. */
  onDataChange?: (rows: TData[]) => void;
  /** Called whenever the unfiltered total row count from the API changes. */
  onTotalChange?: (total: number) => void;
  /**
   * Custom row renderer for non-table layouts (e.g. the Working tab card rows).
   * When provided: DataToolbar and DataTable are bypassed; WorkingTablePagination
   * is rendered instead. The caller owns its own toolbar/search UI.
   */
  renderRows?: (data: TData[]) => ReactNode;
}

const EMPTY_ROWS: unknown[] = [];

export function ApiDatatable<TData, TParams extends ApiDatatableBaseParams>({
  searchParams,
  onNavigate,
  queryKey,
  queryFn,
  columns,
  renderTile,
  viewMode: controlledViewMode,
  onViewModeChange,
  getRowId,
  onRowClick,
  toolbarExtra,
  searchPlaceholder,
  searchAriaLabel,
  emptyMessage,
  emptyState,
  enabled = true,
  getSubRows,
  renderAfterSubRows,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  filterFn,
  onDataChange,
  onTotalChange,
  renderRows,
}: ApiDatatableProps<TData, TParams>) {
  const { t } = useLingui();

  const [localSearch, setLocalSearch] = useState(searchParams.search ?? "");
  const [debouncedSearch] = useDebounce(localSearch, 500);
  const [localViewMode, setLocalViewMode] = useState<DataToolbarViewMode>("table");
  const viewMode = controlledViewMode ?? localViewMode;
  const setViewMode = (mode: DataToolbarViewMode) => {
    setLocalViewMode(mode);
    onViewModeChange?.(mode);
  };
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => getInitialVisibility(columns));
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    getInitialOrder(columns),
  );

  // Keep user-defined order, while ensuring the current column set is always representable.
  const effectiveColumnOrder = useMemo(() => {
    const initialOrder = getInitialOrder(columns);
    const known = columnOrder.filter((id) => initialOrder.includes(id));
    const missing = initialOrder.filter((id) => !known.includes(id));
    return [...known, ...missing];
  }, [columnOrder, columns]);

  // Keep onNavigate stable in the debounce effect without forcing the parent to useCallback.
  const navigateRef = useRef(onNavigate);
  useLayoutEffect(() => {
    navigateRef.current = onNavigate;
  });

  useEffect(() => {
    if (debouncedSearch !== (searchParams.search ?? "")) {
      navigateRef.current((prev) => ({
        ...prev,
        search: debouncedSearch || undefined,
        page: 1,
      }));
    }
  }, [debouncedSearch, searchParams.search]);

  const {
    data: response,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [...queryKey, searchParams],
    queryFn: () => queryFn(searchParams),
    enabled,
    placeholderData: keepPreviousData,
  });

  const rawRows: TData[] = (response?.data ?? EMPTY_ROWS) as TData[];
  const rows: TData[] = useMemo(
    () => (filterFn ? filterFn(rawRows) : rawRows),
    [filterFn, rawRows],
  );
  const totalPages = response?.meta.totalPages ?? 0;
  const totalRows = response?.meta.total;

  const onDataChangeRef = useRef(onDataChange);
  useLayoutEffect(() => {
    onDataChangeRef.current = onDataChange;
  });
  useEffect(() => {
    onDataChangeRef.current?.(rows);
  }, [rows]);

  const onTotalChangeRef = useRef(onTotalChange);
  useLayoutEffect(() => {
    onTotalChangeRef.current = onTotalChange;
  });
  useEffect(() => {
    if (totalRows !== undefined) {
      onTotalChangeRef.current?.(totalRows);
    }
  }, [totalRows]);

  const pagination = {
    pageIndex: searchParams.page - 1,
    pageSize: searchParams.pageSize,
  };

  const sorting = searchParams.sortBy
    ? [{ id: searchParams.sortBy, desc: searchParams.sortOrder === "desc" }]
    : [];

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-[10px]" />
        ))}
      </div>
    );
  }

  // ── Custom row renderer (Working-tab style card rows) ─────────────────────
  if (renderRows) {
    return (
      <div className="flex flex-col">
        <div className="relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
              <Loader2 className="h-8 w-8 animate-spin text-(--color-action)" />
            </div>
          )}
          {rows.length > 0 ? (
            renderRows(rows)
          ) : (
            emptyState ?? (
              <p className="py-16 text-center text-sm text-(--color-text-secondary)">
                {emptyMessage ?? t`No results found`}
              </p>
            )
          )}
        </div>
        {totalPages > 1 && (
          <WorkingTablePagination
            page={searchParams.page}
            pageCount={totalPages}
            onPage={(p) => onNavigate((prev) => ({ ...prev, page: p }))}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <DataToolbar
        searchQuery={localSearch}
        onSearchChange={setLocalSearch}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        searchPlaceholder={searchPlaceholder}
        searchAriaLabel={searchAriaLabel}
        showViewToggle={!!renderTile}
      >
        {toolbarExtra}
        <ColumnSelector
          columns={columns}
          visibility={columnVisibility}
          onVisibilityChange={(id, visible) =>
            setColumnVisibility((prev) => ({ ...prev, [id]: visible }))
          }
          order={effectiveColumnOrder}
          onOrderChange={setColumnOrder}
        />
      </DataToolbar>

      {viewMode === "table" ? (
        <div className="relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
              <Loader2 className="h-8 w-8 animate-spin text-(--color-action)" />
            </div>
          )}
          <DataTable
            columns={columns}
            data={rows}
            emptyMessage={emptyMessage ?? t`No results found`}
            emptyState={emptyState}
            onRowClick={enableRowSelection ? undefined : onRowClick}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            columnOrder={effectiveColumnOrder}
            onColumnOrderChange={setColumnOrder}
            totalRows={totalRows}
            enablePagination
            manualPagination
            pageCount={totalPages}
            pagination={pagination}
            onPaginationChange={(updater) => {
              const next =
                typeof updater === "function" ? updater(pagination) : updater;
              const pageSizeChanged = next.pageSize !== pagination.pageSize;
              onNavigate((prev) => ({
                ...prev,
                page: pageSizeChanged ? 1 : next.pageIndex + 1,
                pageSize: next.pageSize,
              }));
            }}
            manualSorting
            sorting={sorting}
            onSortingChange={(updater) => {
              const next =
                typeof updater === "function" ? updater(sorting) : updater;
              const sort = next[0];
              onNavigate((prev) => ({
                ...prev,
                sortBy: sort?.id,
                sortOrder: sort ? (sort.desc ? "desc" : "asc") : undefined,
              }));
            }}
            getSubRows={getSubRows}
            renderAfterSubRows={renderAfterSubRows}
            enableRowSelection={enableRowSelection}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            getRowId={getRowId}
          />
        </div>
      ) : (
        <div className="relative flex flex-col gap-3">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
              <Loader2 className="h-8 w-8 animate-spin text-(--color-action)" />
            </div>
          )}
          {rows.length > 0 ? (
            rows.map((item, i) => (
              <div key={getRowId ? getRowId(item, i) : String(i)}>
                {renderTile!(item, i)}
              </div>
            ))
          ) : (
            emptyState ?? (
              <p className="py-16 text-center text-sm text-(--color-text-secondary)">
                {emptyMessage ?? t`No results found`}
              </p>
            )
          )}
        </div>
      )}
    </>
  );
}
